import type { LucideIcon } from "lucide-react";
import {
  MessageCircle,
  FileText,
  ClipboardCheck,
  Pencil,
  BookOpen,
  Briefcase,
  Scale,
  ChartNoAxesColumnIncreasing,
  LibraryBig,
  FolderOpen,
  Link2,
  Plus,
  FlaskConical,
  Handshake,
  FolderKanban,
} from "lucide-react";
import type { ActiveSection } from "@/types/appNavigation";
import type { User } from "@/context/UserProvider";
import { userHasDevelopmentSuite } from "@/lib/developmentSuiteAccess";

export type ToolCategoryId = "research" | "trust" | "permitting" | "documents";

export type ToolCategory = {
  id: ToolCategoryId;
  label: string;
  description: string;
};

export const TOOL_CATEGORIES: ToolCategory[] = [
  {
    id: "research",
    label: "Research & insights",
    description: "Answer code questions, track policy, and review constituent trends",
  },
  {
    id: "trust",
    label: "Community & residents",
    description: "Resident concerns, profiles, and feedback",
  },
  {
    id: "permitting",
    label: "Permitting & plans",
    description: "Review plans, process applications, and look up cases",
  },
  {
    id: "documents",
    label: "Documents & resources",
    description: "Draft reports, browse files, and manage municipal links",
  },
];

export type LaunchPadTool = {
  section: ActiveSection;
  label: string;
  description: string;
  icon: LucideIcon;
  category: ToolCategoryId;
  isEnabled: (user: User) => boolean;
  /** Omit from the explorer instead of showing an unlock hint while disabled. */
  hiddenWhenDisabled?: boolean;
};

export const LAUNCH_PAD_TOOLS: LaunchPadTool[] = [
  {
    section: "chat",
    category: "research",
    label: "Research Assistant",
    description: "Ask questions about your municipal code and policy",
    icon: MessageCircle,
    isEnabled: () => true,
  },
  {
    section: "code_library",
    category: "research",
    label: "Code Library",
    description: "Compare regulatory approaches across municipalities",
    icon: LibraryBig,
    isEnabled: (user) => Boolean(user.code_library_access),
  },
  {
    section: "legislation_monitor",
    category: "research",
    label: "Legislation Monitor",
    description: "Prepare for upcoming state laws taking effect",
    icon: Scale,
    isEnabled: (user) => Boolean(user.legislation_monitor_access),
  },
  {
    section: "sandbox",
    category: "research",
    label: "Sandbox",
    description: "Experiment with models and prompts outside production workflows",
    icon: FlaskConical,
    isEnabled: (user) => Boolean(user.sandbox_access),
  },
  {
    section: "analytics",
    category: "research",
    label: "Connect Analytics",
    description: "Review trends in constituent questions",
    icon: ChartNoAxesColumnIncreasing,
    isEnabled: (user) => Boolean(user.connect_analytics),
  },
  {
    section: "grant_suite",
    category: "research",
    label: "Grants",
    description: "Track grant opportunities and discover funding",
    icon: BookOpen,
    isEnabled: (user) => Boolean(user.grant_discover ?? (user.grant_access || user.grant_suite)),
  },
  {
    section: "permit_suite",
    category: "permitting",
    label: "Permit Suite",
    description: "Track permits through your review pipeline",
    icon: ClipboardCheck,
    isEnabled: (user) => Boolean(user.permit_suite),
  },
  {
    section: "development_suite",
    category: "permitting",
    label: "Development Suite",
    description: "Organize projects and their permit and planning records",
    icon: FolderKanban,
    isEnabled: userHasDevelopmentSuite,
    hiddenWhenDisabled: true,
  },
  {
    section: "trust_suite",
    category: "trust",
    label: "Community Suite",
    description: "Work resident concerns and manage resident profiles",
    icon: Handshake,
    isEnabled: (user) => Boolean(user.trust_suite),
  },
  {
    section: "plan_review",
    category: "permitting",
    label: "Plan Review",
    description: "Analyze submitted site plans against your code",
    icon: ClipboardCheck,
    isEnabled: (user) => Boolean(user.plan_review_access),
  },
  {
    section: "form_extraction",
    category: "permitting",
    label: "Permit Assistant",
    description: "Extract and validate permit application fields",
    icon: Pencil,
    isEnabled: (user) => Boolean(user.form_extraction_ui),
  },
  {
    section: "case_portal",
    category: "permitting",
    label: "Case Portal",
    description: "Look up permitting cases and history",
    icon: Briefcase,
    isEnabled: (user) => Boolean(user.case_portal_access),
  },
  {
    section: "staff_reports",
    category: "documents",
    label: "Report Assistant",
    description: "Draft council-ready staff reports",
    icon: FileText,
    isEnabled: (user) => Boolean(user.staff_reports_access),
  },
  {
    section: "file_viewer",
    category: "documents",
    label: "File Center",
    description: "Browse municipal PDFs and document folders",
    icon: FolderOpen,
    isEnabled: (user) => Boolean(user.file_viewer),
  },
  {
    section: "municipality_resource_links",
    category: "documents",
    label: "Resource Links",
    description: "Quick access to curated municipal links",
    icon: Link2,
    isEnabled: (user) => Boolean(user.municipality_resource_links_access),
  },
];

export type LaunchPadQuickAction = {
  id: string;
  label: string;
  icon: LucideIcon;
  section: ActiveSection;
  isEnabled: (user: User) => boolean;
  openNewResearchChat?: boolean;
};

export const LAUNCH_PAD_QUICK_ACTIONS: LaunchPadQuickAction[] = [
  {
    id: "new-chat",
    label: "New research chat",
    icon: Plus,
    section: "chat",
    isEnabled: () => true,
    openNewResearchChat: true,
  },
  {
    id: "plan-review",
    label: "Review a plan",
    icon: ClipboardCheck,
    section: "plan_review",
    isEnabled: (user) => Boolean(user.plan_review_access),
  },
  {
    id: "staff-report",
    label: "Draft a report",
    icon: FileText,
    section: "staff_reports",
    isEnabled: (user) => Boolean(user.staff_reports_access),
  },
  {
    id: "permit",
    label: "Process a permit",
    icon: Pencil,
    section: "form_extraction",
    isEnabled: (user) => Boolean(user.form_extraction_ui),
  },
  {
    id: "files",
    label: "Open File Center",
    icon: FolderOpen,
    section: "file_viewer",
    isEnabled: (user) => Boolean(user.file_viewer),
  },
  {
    id: "grants",
    label: "Open Grants",
    icon: BookOpen,
    section: "grant_suite",
    isEnabled: (user) => Boolean(user.grant_discover ?? (user.grant_access || user.grant_suite)),
  },
];

/** Shown on tools and insight widgets the account does not have yet. */
export const LAUNCH_PAD_UNAVAILABLE_HINT =
  "Contact your Customer Success Manager to unlock this feature.";

export const RESUME_LIMITS = {
  recentFeed: 5,
} as const;

export const QUICK_ACTION_LIMIT = 4;

/** @deprecated Use LAUNCH_PAD_TOOLS — kept for tests migrating */
export const LAUNCH_PAD_TASKS = LAUNCH_PAD_TOOLS.filter((t) =>
  ["chat", "plan_review", "staff_reports", "form_extraction", "grant_suite", "case_portal", "legislation_monitor", "analytics"].includes(
    t.section,
  ),
).map((t) => ({
  section: t.section,
  taskLabel: t.label,
  description: t.description,
  icon: t.icon,
  isEnabled: t.isEnabled,
}));

/** @deprecated Use LAUNCH_PAD_TOOLS */
export const LAUNCH_PAD_RESOURCES = LAUNCH_PAD_TOOLS.filter((t) =>
  ["file_viewer", "municipality_resource_links", "code_library"].includes(t.section),
).map((t) => ({
  section: t.section,
  label: t.label,
  description: t.description,
  icon: t.icon,
  isEnabled: t.isEnabled,
}));

export function toolsForCategory(user: User, categoryId: ToolCategoryId): LaunchPadTool[] {
  return LAUNCH_PAD_TOOLS.filter(
    (t) => t.category === categoryId && (!t.hiddenWhenDisabled || t.isEnabled(user)),
  );
}

export function enabledToolsForCategory(user: User, categoryId: ToolCategoryId): LaunchPadTool[] {
  return toolsForCategory(user, categoryId).filter((t) => t.isEnabled(user));
}

export function enabledToolCount(user: User): number {
  return LAUNCH_PAD_TOOLS.filter((t) => t.isEnabled(user)).length;
}
